import {
  Alert,
  Box,
  Button,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCallback, useMemo } from "react";

import { type PieceBackfillSurveyQuery } from "../../generated/graphql";
import { describeSurveyError } from "./piece-backfill-errors";

const styles = {
  root: { mt: 4 },
  subtitle: { mt: 0.5, mb: 2 },
  alert: { mb: 2, maxWidth: 720 },
  table: { maxWidth: 900 },
  totals: { mt: 2 },
} satisfies Record<string, SxProps<Theme>>;

/** The survey payload, or undefined before the first survey. */
export type PieceBackfillSurveyValue =
  | PieceBackfillSurveyQuery["pieceBackfillSurvey"]
  | undefined;

/** Props for {@link PieceBackfillSurveyPanel}. */
export type PieceBackfillSurveyPanelProps = {
  /** The latest survey result, or undefined before the first survey. */
  readonly survey: PieceBackfillSurveyValue;
  /** True while the survey query is in flight. */
  readonly loading: boolean;
  /** Message from a failed survey, or undefined when the last survey succeeded. */
  readonly errorMessage?: string;
  /** True when a company and cutoff are selected. */
  readonly canSurvey: boolean;
  /** Runs the survey. */
  readonly onSurvey: () => void;
  /** Billing parties the run is narrowed to; empty means all of them. */
  readonly selectedContactUuids: readonly string[];
  /** Called with the new narrowing selection. */
  readonly onSelectionChange: (contactUuids: string[]) => void;
  /** True while a run is in flight, which freezes the selection. */
  readonly disabled: boolean;
};

/**
 * Read-only "what would this touch" view. Counts the in-scope packages per
 * billing party and the pieces they are missing, and lets the operator narrow a
 * run to a subset of billing parties.
 *
 * Selecting nothing means every billing party, which is what the backend does
 * with an empty narrowing list.
 */
const PieceBackfillSurveyPanel = ({
  survey,
  loading,
  errorMessage,
  canSurvey,
  onSurvey,
  selectedContactUuids,
  onSelectionChange,
  disabled,
}: PieceBackfillSurveyPanelProps) => {
  const selected = useMemo(
    () => new Set(selectedContactUuids),
    [selectedContactUuids],
  );

  const rows = useMemo(() => {
    const list = survey?.billingParties ?? [];
    return [...list].sort((a, b) => b.missingPieceCount - a.missingPieceCount);
  }, [survey]);

  const toggleRow = useCallback(
    (contactUuid: string) => {
      const next = new Set(selected);
      if (next.has(contactUuid)) {
        next.delete(contactUuid);
      } else {
        next.add(contactUuid);
      }
      onSelectionChange([...next]);
    },
    [onSelectionChange, selected],
  );

  const clearSelection = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  return (
    <Box sx={styles.root}>
      <Typography variant="subtitle1">1. Survey</Typography>
      <Typography color="text.secondary" variant="body2" sx={styles.subtitle}>
        Counts what a run at this cutoff would find. Reads only, writes nothing.
      </Typography>

      {errorMessage === undefined ? null : (
        <Alert severity="error" sx={styles.alert}>
          {describeSurveyError(errorMessage)}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          disabled={!canSurvey || loading}
          onClick={onSurvey}
        >
          {loading ? "Surveying…" : "Run survey"}
        </Button>
        {selected.size === 0 ? null : (
          <Button size="small" disabled={disabled} onClick={clearSelection}>
            Clear selection ({selected.size})
          </Button>
        )}
      </Stack>

      {survey === undefined ? null : (
        <>
          <Typography variant="body2" color="text.secondary" sx={styles.totals}>
            {survey.totalPackageCount} in-scope packages covering{" "}
            {survey.totalPackageQuantity} units,{" "}
            {survey.totalExistingPieceCount} pieces already tracked,{" "}
            {survey.totalMissingPieceCount} missing.
          </Typography>

          {rows.length === 0 ? (
            <Alert severity="info" sx={styles.alert}>
              No in-scope packages at this cutoff. Nothing to backfill.
            </Alert>
          ) : (
            <Table size="small" sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Only</TableCell>
                  <TableCell>Billing party</TableCell>
                  <TableCell align="right">Packages</TableCell>
                  <TableCell align="right">Units</TableCell>
                  <TableCell align="right">Existing pieces</TableCell>
                  <TableCell align="right">Missing pieces</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.contactUuid}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        disabled={disabled}
                        checked={selected.has(row.contactUuid)}
                        onChange={() => {
                          toggleRow(row.contactUuid);
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.contactName}</TableCell>
                    <TableCell align="right">{row.packageCount}</TableCell>
                    <TableCell align="right">
                      {row.totalPackageQuantity}
                    </TableCell>
                    <TableCell align="right">
                      {row.existingPieceCount}
                    </TableCell>
                    <TableCell align="right">{row.missingPieceCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Box>
  );
};

export default PieceBackfillSurveyPanel;
